import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./hooks/useAuth";
import { useData } from "./hooks/useData";
import { I } from "./components/Icons";
import { Avatar, clsx, daysFromToday } from "./components/Primitives";
import { api } from "./api/client";
import type { Partner, Principal, DashboardCounts, NewDealDraft } from "./types";

import Radar from "./components/Radar";
import Tasks from "./components/Tasks";
import Accounts from "./components/Accounts";
import Inbox from "./components/Inbox";
import PipelineHub from "./components/PipelineHub";
import ProjectDetail from "./components/ProjectDetail";
import PrincipalStatus from "./components/PrincipalStatus";
import Drive from "./components/Drive";
import CommandK from "./components/CommandK";
import AskSaga from "./components/AskSaga";
import Minutes from "./components/Minutes";
import Settings from "./components/Settings";
import Leads from "./components/Leads";
import Newsletters from "./components/Newsletters";
import DealEditor from "./components/DealEditor";
import RelationshipTargets from "./components/RelationshipTargets";
import PhoneShell from "./phone/PhoneShell";
import Today from "./phone/Today";
import PhoneInbox from "./phone/PhoneInbox";
import PhoneContacts from "./phone/PhoneContacts";
import PhonePipeline from "./phone/PhonePipeline";
import PhoneMore, { PhoneInstall, PhoneNotifications } from "./phone/PhoneMore";
import QuickTask from "./phone/QuickTask";
import Web from "./components/Web";
import PhoneWeb from "./phone/PhoneWeb";

const NAV = [
  { id: "tasks", label: "Tasks", icon: I.tasks },
  { id: "radar", label: "Radar", icon: I.radar },
  { id: "clients", label: "Accounts", icon: I.meeting },
  { id: "inbox", label: "Inbox", icon: I.mail },
  { id: "leads", label: "Leads", icon: I.bell },
  { id: "newsletters", label: "Newsletters", icon: I.send },
  { id: "web", label: "Web", icon: I.globe },
  { id: "pipeline", label: "Pipeline", icon: I.pipeline },
  { id: "files", label: "Files", icon: I.doc },
];

interface Route {
  screen: string;
  id?: string | null;
}

/** Routes live in the URL (?screen=…&id=…) so Back works, and a push notification can open a screen. */
function routeFromUrl(): Route | null {
  const q = new URLSearchParams(window.location.search);
  const screen = q.get("screen");
  return screen && /^[a-z-]+$/.test(screen) ? { screen, id: q.get("id") } : null;
}
const urlOf = (r: Route) => {
  const q = new URLSearchParams();
  q.set("screen", r.screen);
  if (r.id) q.set("id", r.id);
  return `${window.location.pathname}?${q}`;
};

/** Phones get their own layout; "Full desktop layout" (?desktop=1) opts out on this device. */
function usePhone(): boolean {
  const mq = "(max-width: 700px)";
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(mq).matches ?? false);
  useEffect(() => {
    const m = window.matchMedia?.(mq);
    if (!m) return;
    const h = () => setNarrow(m.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  const [desktop] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("desktop");
    try {
      if (q === "1") localStorage.setItem("saga-desktop", "1");
      if (q === "0") localStorage.removeItem("saga-desktop");
      return localStorage.getItem("saga-desktop") === "1";
    } catch { return q === "1"; }
  });
  return narrow && !desktop;
}

export default function App() {
  const { user, loading: authLoading } = useAuth();
  const phone = usePhone();
  // Deep links from emails and notifications, e.g. /?screen=newsletters&id=… in the approval email.
  const [route, setRoute] = useState<Route>(() => routeFromUrl() || { screen: phone ? "today" : "radar", id: null });
  useEffect(() => {
    window.history.replaceState(null, "", urlOf(route));
    const h = () => setRoute(routeFromUrl() || { screen: phone ? "today" : "radar", id: null });
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrefill, setAiPrefill] = useState("");
  const [newDeal, setNewDeal] = useState<NewDealDraft | null>(null);
  const [minOpen, setMinOpen] = useState(false);
  const [minPrefill, setMinPrefill] = useState<{ dealId?: string; contactId?: string; orgId?: string } | null>(null);
  const [version, setVersion] = useState(0);

  const { data: counts, refetch: refetchCounts } = useData<DashboardCounts>(user ? "/api/dashboard/counts" : null, [version]);
  // Leads and intel arrive from outside the app — keep the sidebar badges fresh.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(refetchCounts, 60_000);
    return () => clearInterval(t);
  }, [user, refetchCounts]);
  const { data: partners } = useData<Partner[]>(user ? "/api/partners" : null, [version]);
  const { data: principals } = useData<Principal[]>(user ? "/api/principals" : null, [version]);

  // The app icon on the Home Screen shows what needs you (replies, hand-overs, due today).
  useEffect(() => {
    const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (counts?.needs_you === undefined || !nav.setAppBadge) return;
    (counts.needs_you ? nav.setAppBadge(counts.needs_you) : nav.clearAppBadge?.())?.catch(() => {});
  }, [counts?.needs_you]);

  const partnersMap: Record<string, Partner> = {};
  partners?.forEach(p => { partnersMap[p.id] = p; });

  const go = useCallback((screen: string, id?: string | null) => {
    const next = { screen, id: id || null };
    const cur = routeFromUrl();
    if (cur?.screen !== next.screen || (cur?.id || null) !== next.id) window.history.pushState(null, "", urlOf(next));
    setRoute(next);
    const main = document.querySelector(".app-main");
    if (main) main.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);
  const [quickTask, setQuickTask] = useState(false);
  const [newContact, setNewContact] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); }, [toast]);

  const openAI = useCallback((prefill?: string) => {
    setAiPrefill(prefill || "");
    setAiOpen(true);
  }, []);

  const openMinutes = useCallback((prefill?: { dealId?: string; contactId?: string; orgId?: string } | null) => {
    setMinPrefill(prefill || null);
    setMinOpen(true);
  }, []);

  const bump = useCallback(() => setVersion(v => v + 1), []);
  const [meOpen, setMeOpen] = useState(false);
  // Phones: the sidebar slides in over the page instead of taking a fixed column.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [route.screen, route.id]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout", {});
    // Nothing from the CRM stays on the device after signing out.
    navigator.serviceWorker?.controller?.postMessage("clear-cache");
    try { localStorage.removeItem("saga-offline-queue"); } catch { /* blocked storage */ }
    window.location.href = "/";
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen(v => !v); }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") { e.preventDefault(); setAiPrefill(""); setAiOpen(v => !v); }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "m") { e.preventDefault(); setMinPrefill(null); setMinOpen(v => !v); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (authLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "var(--canvas)" }}>
        <div style={{ textAlign: "center", color: "var(--muted)" }}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "var(--canvas)" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: 24, display: "flex", justifyContent: "center" }}>
            <SagaLogo />
          </div>
          <a href="/auth/google" className="btn primary" style={{ textDecoration: "none" }}>Sign in with Google</a>
        </div>
      </div>
    );
  }

  const me: Partner = partnersMap[user.id] || { id: user.id, name: user.name, initials: "?", role: "", base: "", email: "", photo: "" };

  const radarN = counts?.radar || 0;
  const taskN = counts?.tasks || 0;
  const reviewN = counts?.inbox || 0;

  const overlays = (
    <>
      <CommandK open={cmdOpen} close={() => setCmdOpen(false)} go={go} openAI={openAI} openMinutes={openMinutes} />
      <AskSaga open={aiOpen} prefill={aiPrefill} close={() => setAiOpen(false)} bump={bump} partners={partnersMap} onNewDeal={setNewDeal} />
      {newDeal && (
        <DealEditor draft={newDeal} partners={partnersMap} onClose={() => setNewDeal(null)} onSaved={bump}
          onCreated={id => go("project", id)} />
      )}
      <Minutes open={minOpen} prefill={minPrefill} close={() => setMinOpen(false)} go={go} bump={bump} partners={partnersMap} />
      {quickTask && <QuickTask partners={partnersMap} myId={user.id} onClose={() => setQuickTask(false)} onSaved={m => { setQuickTask(false); setToast(m); bump(); }} />}
      {toast && <div className="ph-toast" role="status">{toast}</div>}
    </>
  );

  if (phone) {
    const s = route.screen, id = route.id || null;
    let pbody;
    if (s === "today") pbody = <Today go={go} partners={partnersMap} myId={user.id} onChanged={bump} />;
    else if (s === "tasks") pbody = <Tasks go={go} openAI={openAI} bump={bump} partners={partnersMap} myId={user.id} focusId={id || undefined} phone />;
    else if (s === "inbox") pbody = <PhoneInbox go={go} onChanged={bump} />;
    else if (s === "inbox-all") pbody = <Inbox key={id || "inbox"} go={go} bump={bump} partners={partnersMap} myId={user.id} focusId={id || undefined} phone />;
    else if (s === "contacts") pbody = <PhoneContacts key={id || "list"} id={id} go={go} partners={partnersMap} openMinutes={openMinutes} newContact={newContact} onDone={() => setNewContact(false)} />;
    else if (s === "pipeline") pbody = <PhonePipeline go={go} partners={partnersMap} myId={user.id} />;
    else if (s === "project") pbody = <ProjectDetail id={id!} go={go} openAI={openAI} openMinutes={openMinutes} bump={bump} partners={partnersMap} />;
    else if (s === "targets") pbody = <div className="ph-pad"><RelationshipTargets partners={partnersMap} go={go} /></div>;
    else if (s === "web") pbody = <PhoneWeb go={go} />;
    else if (s === "notifications") pbody = <PhoneNotifications />;
    else if (s === "install") pbody = <PhoneInstall />;
    else if (s === "clients" || s === "accounts") pbody = <Accounts key={id || "list"} go={go} openMinutes={openMinutes} focusId={id} partners={partnersMap} phone />;
    else if (s === "countries") pbody = <PipelineHub go={go} openAI={openAI} initialTab="countries" partners={partnersMap} />;
    else if (s === "principals") pbody = <PipelineHub go={go} openAI={openAI} initialTab="principals" partners={partnersMap} />;
    else if (s === "principal") pbody = <PrincipalStatus id={id!} go={go} openMinutes={openMinutes} partners={partnersMap} />;
    else if (s === "files") pbody = <Drive go={go} />;
    else if (s === "newsletters") pbody = <Newsletters key={id || "list"} partners={partnersMap} me={user.id} isAdmin={!!user.is_admin} initialId={id} />;
    else if (s === "leads") pbody = <Leads go={go} partners={partnersMap} bump={bump} />;
    else if (s === "radar") pbody = <Radar go={go} bump={bump} partners={partnersMap} me={user.id} isAdmin={!!user.is_admin} />;
    else if (s === "settings" && user.is_admin) pbody = <Settings key={id || "settings"} myId={user.id} onChanged={bump} partnersMap={partnersMap} initialTab={id} />;
    else pbody = <PhoneMore go={go} me={me} counts={counts} onLogout={logout} />;
    const capture = async (k: "minutes" | "task" | "contact" | "paste") => {
      if (k === "minutes") openMinutes(null);
      else if (k === "task") setQuickTask(true);
      else if (k === "contact") { setNewContact(true); go("contacts"); }
      else {
        // Paste: whatever is on the clipboard goes to Ask Saga, which files it.
        let text = "";
        try { text = (await navigator.clipboard?.readText?.()) || ""; } catch { /* permission refused — paste by hand */ }
        openAI(text ? `File this in the CRM:\n\n${text}` : "");
      }
    };
    return (
      <>
        <PhoneShell screen={s === "accounts" ? "clients" : s} go={go}
          badges={{ tasks: taskN, inbox: reviewN, today: counts?.needs_you || 0 }}
          onSearch={() => setCmdOpen(true)} onAsk={() => openAI("")} onCapture={capture}>
          {pbody}
        </PhoneShell>
        {overlays}
      </>
    );
  }

  let body;
  if (route.screen === "project") body = <ProjectDetail id={route.id!} go={go} openAI={openAI} openMinutes={openMinutes} bump={bump} partners={partnersMap} />;
  else if (route.screen === "tasks") body = <Tasks go={go} openAI={openAI} bump={bump} partners={partnersMap} myId={user.id} focusId={route.id || undefined} />;
  else if (route.screen === "clients" || route.screen === "accounts") body = <Accounts go={go} openMinutes={openMinutes} focusId={route.id || null} partners={partnersMap} />;
  else if (route.screen === "inbox-all") body = <Inbox key={route.id || "inbox"} go={go} bump={bump} partners={partnersMap} myId={user.id} focusId={route.id || undefined} />;
  else if (route.screen === "contacts") body = <Accounts go={go} openMinutes={openMinutes} focusId={null} partners={partnersMap} />;
  else if (route.screen === "targets") body = <div className="page"><RelationshipTargets partners={partnersMap} go={go} /></div>;
  else if (route.screen === "notifications") body = <div className="page" style={{ maxWidth: 560 }}><PhoneNotifications /></div>;
  else if (route.screen === "install") body = <div className="page" style={{ maxWidth: 560 }}><PhoneInstall /></div>;
  else if (route.screen === "web") body = <Web go={go} isAdmin={!!user.is_admin} />;
  else if (route.screen === "today") body = <div className="page" style={{ maxWidth: 640 }}><Today go={go} partners={partnersMap} myId={user.id} onChanged={bump} /></div>;
  else if (route.screen === "inbox") body = <Inbox key={route.id || "inbox"} go={go} bump={bump} partners={partnersMap} myId={user.id} focusId={route.id || undefined} />;
  else if (route.screen === "pipeline") body = <PipelineHub go={go} openAI={openAI} initialTab="deals" partners={partnersMap} />;
  else if (route.screen === "countries") body = <PipelineHub go={go} openAI={openAI} initialTab="countries" partners={partnersMap} />;
  else if (route.screen === "files") body = <Drive go={go} />;
  else if (route.screen === "principal") body = <PrincipalStatus id={route.id!} go={go} openMinutes={openMinutes} partners={partnersMap} />;
  else if (route.screen === "newsletters") body = <Newsletters key={route.id || "list"} partners={partnersMap} me={user.id} isAdmin={!!user.is_admin} initialId={route.id} />;
  else if (route.screen === "leads") body = <Leads go={go} partners={partnersMap} bump={bump} />;
  else if (route.screen === "settings" && user.is_admin) body = <Settings key={route.id || "settings"} myId={user.id} onChanged={bump} partnersMap={partnersMap} initialTab={route.id} />;
  else if (route.screen === "principals") body = <PipelineHub go={go} openAI={openAI} initialTab="principals" partners={partnersMap} />;
  else body = <Radar go={go} bump={bump} partners={partnersMap} me={user.id} isAdmin={!!user.is_admin} />;

  return (
    <div className={clsx("app", navOpen && "nav-open")}>
      {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}
      <aside className="sidebar">
        <div className="brand">
          <SagaLogo />
        </div>

        <button className="ask-btn" onClick={() => openAI("")}>
          <span className="ask-ic">{I.ai({ size: 16 })}</span>
          <span className="ask-label">Ask Saga</span>
          <span className="kbd-chip">⌘J</span>
        </button>

        <button className="minutes-btn" onClick={() => openMinutes(null)}>
          {I.note({ size: 15, w: 1.8 })}
          <span className="ask-label">New minutes</span>
          <span className="kbd-chip">⌘M</span>
        </button>

        <button className="side-search" onClick={() => setCmdOpen(true)}>
          {I.search({ size: 15, w: 1.8 })}
          <span>Search</span>
          <span className="kbd-chip">⌘K</span>
        </button>

        <nav className="side-nav">
          {NAV.map(n => (
            <button key={n.id}
              className={clsx("nav-item", (route.screen === n.id || (n.id === "pipeline" && ["project", "countries", "principals"].includes(route.screen))) && "active")}
              onClick={() => go(n.id)}>
              <span className="nav-ic">{n.icon({ size: 18 })}</span>
              <span className="nav-label">{n.label}</span>
              {n.id === "radar" && radarN > 0 && <span className="nav-badge">{radarN}</span>}
              {n.id === "tasks" && taskN > 0 && <span className="nav-badge warn">{taskN}</span>}
              {n.id === "inbox" && reviewN > 0 && <span className="nav-badge alt">{reviewN}</span>}
              {n.id === "leads" && (counts?.leads || 0) > 0 && <span className="nav-badge">{counts?.leads}</span>}
            </button>
          ))}
          {user.is_admin && (
            <button className={clsx("nav-item", route.screen === "settings" && "active")} onClick={() => go("settings")}>
              <span className="nav-ic">{I.sliders({ size: 18 })}</span>
              <span className="nav-label">Settings</span>
            </button>
          )}
        </nav>

        {principals && principals.length > 0 && (
          <div className="side-section">
            <div className="side-section-label">Principals</div>
            {principals.map(p => (
              <button key={p.id} className="side-prin" onClick={() => go("principal", p.id)}>
                <span className="side-prin-dot" style={{ background: p.color }} />
                {p.name}
                {I.arrowRight({ size: 13, w: 1.8, className: "side-prin-go" })}
              </button>
            ))}
          </div>
        )}

        <div className="side-foot">
          {meOpen && (
            <div className="me-menu">
              {user.is_admin && (
                <button className="me-menu-item" onClick={() => { setMeOpen(false); go("settings"); }}>
                  {I.sliders({ size: 13, w: 1.8 })}
                  Settings
                </button>
              )}
              {window.matchMedia?.("(max-width: 700px)").matches && (
                <button className="me-menu-item" onClick={() => { window.location.href = "/?desktop=0"; }}>
                  {I.home({ size: 13, w: 1.8 })}
                  Phone layout
                </button>
              )}
              <button className="me-menu-item" onClick={logout}>
                {I.ext({ size: 13, w: 1.8 })}
                Sign out
              </button>
            </div>
          )}
          <div className="me-row" style={{ cursor: "pointer" }} onClick={() => setMeOpen(v => !v)}>
            <Avatar partner={me} size={30} />
            <div className="me-body">
              <div className="me-name">{me.name}</div>
              <div className="me-role">{me.role}</div>
            </div>
            <span className="me-base">{me.base}</span>
          </div>
        </div>
      </aside>

      <main className="app-main">
        <div className="mobile-bar">
          <button className="mobile-menu" aria-label="Menu" onClick={() => setNavOpen(true)}>
            <span /><span /><span />
            {(taskN + reviewN) > 0 && <i className="mobile-dot" />}
          </button>
          <span className="mobile-title">{NAV.find(n => n.id === route.screen)?.label || (route.screen === "settings" ? "Settings" : "Saga")}</span>
          <button className="mobile-ask" onClick={() => openAI("")} aria-label="Ask Saga">{I.ai({ size: 17 })}</button>
          <button className="mobile-ask" onClick={() => setCmdOpen(true)} aria-label="Search">{I.search({ size: 17 })}</button>
        </div>
        {body}
      </main>

      {overlays}
    </div>
  );
}

function SagaIcon({ size = 34 }: { size?: number }) {
  return <img src="/favicon.png" alt="Saga" style={{ width: size, height: size, display: "block" }} />;
}

function SagaLogo() {
  return <img src="/logo-saga.png" alt="Saga Advisory" className="brand-logo" style={{ height: 38, width: "auto", display: "block" }} />;
}
